import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import {
	useAutomationTemplateContactStepsQuery,
	useAutomationTemplateEligibleContactsQuery,
} from '../../../../queries/Automation';
import { useContactSearchQuery } from '../../../../queries/Contact/useContactSearchQuery';
import { Button } from '../../../../web/components-v2/Button';
import { Input, InputWrapper } from '../../../../web/components-v2/Input';
import { SearchIcon2 } from '../../../../web/components/svgs/icons/SearchIcon2';
import { CONTACT_COLORS } from '../../../../web/containers/AutomationFlows/types/contacts';
import { ContactCard } from '../ContactCard';
import { ContactProgressIndicator } from './ContactProgressIndicator';
import { styleSheet } from './styles';
import { TestContact } from './types';

interface TestingContactsDrawerContentProps {
	templateId: string;
	selectedContacts: TestContact[];
	onContactsChange: (contacts: TestContact[]) => void;
	impersonationContext?: Api.IImpersonationContext;
	useDraft: boolean;
}

const MAX_CONTACTS = 5;

export const TestingContactsDrawerContent: React.FC<TestingContactsDrawerContentProps> = ({
	templateId,
	selectedContacts,
	onContactsChange,
	impersonationContext,
	useDraft,
}) => {
	const { data: eligibleContactsData, isLoading: isLoadingEligible } = useAutomationTemplateEligibleContactsQuery({
		templateId,
		limit: MAX_CONTACTS,
		impersonationContext,
		useDraft,
	});

	const eligibleContacts = React.useMemo(() => eligibleContactsData?.values ?? [], [eligibleContactsData?.values]);
	const eligibleContactsTotal = eligibleContactsData?.totalCount ?? eligibleContacts.length;
	const maxReached = selectedContacts.length >= MAX_CONTACTS;
	const remainingSlots = Math.max(MAX_CONTACTS - selectedContacts.length, 0);

	const selectedContactIds = React.useMemo(
		() => new Set(selectedContacts.map(tc => tc.contact.id)),
		[selectedContacts]
	);

	const availableEligibleContacts = React.useMemo(
		() =>
			eligibleContacts.filter((contact: Api.IContact) => !selectedContactIds.has(contact.id)).slice(0, remainingSlots),
		[eligibleContacts, selectedContactIds, remainingSlots]
	);

	const [searchInput, setSearchInput] = React.useState('');
	const debouncedSearch = useDebounceValue(searchInput.trim(), 400);
	const shouldSearch = debouncedSearch.length >= 2 && !maxReached;
	const {
		data: contactSearchData,
		isFetching: isSearchingContacts,
		isError: isSearchError,
	} = useContactSearchQuery({
		searchQuery: debouncedSearch,
		pageSize: MAX_CONTACTS,
		enabled: shouldSearch,
		impersonationContext,
	});

	const filteredSearchResults = React.useMemo(() => {
		return (contactSearchData?.values || []).filter((contact: Api.IContact) => !selectedContactIds.has(contact.id));
	}, [contactSearchData?.values, selectedContactIds]);

	const handleAddContact = (contact: Api.IContact) => {
		if (maxReached || selectedContactIds.has(contact.id)) {
			return;
		}

		const color = CONTACT_COLORS[selectedContacts.length % CONTACT_COLORS.length];
		const newTestContact: TestContact = {
			contact,
			color,
			isSelected: true,
			steps: undefined,
		};

		onContactsChange([...selectedContacts, newTestContact]);
	};

	const handleToggleSelect = (contactId: string) => {
		const existingIndex = selectedContacts.findIndex(tc => tc.contact.id === contactId);
		if (existingIndex < 0) {
			return;
		}

		const updated = selectedContacts
			.filter((_, index) => index !== existingIndex)
			.map((tc, index) => ({ ...tc, color: CONTACT_COLORS[index % CONTACT_COLORS.length] }));
		onContactsChange(updated);
	};

	const handleClearAll = () => {
		onContactsChange([]);
	};

	const renderContactOptionRow = (contact: Api.IContact, source: 'search' | 'eligible') => {
		const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unnamed Contact';

		return (
			<li key={`${source}-${contact.id}`} className={css(styleSheet.contactOptionRow)}>
				<div className={css(styleSheet.contactOptionInfo)}>
					<div className={css(styleSheet.contactOptionName)}>{fullName}</div>
					{contact.primaryEmail?.value ? (
						<div className={css(styleSheet.contactOptionEmail)}>{contact.primaryEmail.value}</div>
					) : (
						<div className={css(styleSheet.contactOptionSecondaryText)}>No primary email</div>
					)}
				</div>
				<Button variant='secondary' size='sm' onClick={() => handleAddContact(contact)} disabled={maxReached}>
					Add
				</Button>
			</li>
		);
	};

	const renderSearchResults = () => {
		if (maxReached) {
			return <p className={css(styleSheet.searchHint)}>Remove a selected contact to add another.</p>;
		}

		if (!searchInput.trim()) {
			return <p className={css(styleSheet.searchHint)}>Search by contact name or email address.</p>;
		}

		if (debouncedSearch.length < 2) {
			return <p className={css(styleSheet.searchHint)}>Enter at least two characters to search.</p>;
		}

		if (isSearchingContacts) {
			return <p className={css(styleSheet.emptyState)}>Searching contacts...</p>;
		}

		if (isSearchError) {
			return <p className={css(styleSheet.emptyState)}>Unable to search contacts. Please try again.</p>;
		}

		if (filteredSearchResults.length === 0) {
			return <p className={css(styleSheet.emptyState)}>No contacts found.</p>;
		}

		return (
			<ul className={css(styleSheet.contactOptions)}>
				{filteredSearchResults.map(contact => renderContactOptionRow(contact, 'search'))}
			</ul>
		);
	};

	return (
		<div className={css(styleSheet.drawerContent)}>
			<section className={css(styleSheet.section)}>
				<h3 className={css(styleSheet.sectionHeader)}>Search Contacts</h3>
				<p className={css(styleSheet.sectionDescription)}>
					Find contacts by name or email. Search uses the current impersonated account so testing reflects real data.
				</p>
				<div className={css(styleSheet.searchInputRow)}>
					<InputWrapper styles={[styleSheet.searchInputWrapper]}>
						<SearchIcon2 width={16} height={16} />
						<Input
							type='text'
							id='automation-testing-contact-search'
							value={searchInput}
							onChange={e => setSearchInput(e.target.value)}
							placeholder={maxReached ? 'Contact limit reached' : 'Search by name or email'}
							disabled={maxReached}
						/>
					</InputWrapper>
					{searchInput ? (
						<Button variant='ghost' size='sm' onClick={() => setSearchInput('')}>
							Clear
						</Button>
					) : null}
				</div>
				{renderSearchResults()}
			</section>

			<section className={css(styleSheet.section)}>
				<div className={css(styleSheet.sectionHeaderRow)}>
					<h3 className={css(styleSheet.sectionHeader)}>Eligible Contacts</h3>
					{eligibleContactsTotal > 0 ? (
						<span className={css(styleSheet.sectionMeta)}>{eligibleContactsTotal} total eligible</span>
					) : null}
				</div>
				<p className={css(styleSheet.sectionDescription)}>
					These contacts currently match your trigger criteria and can be added quickly for testing.
				</p>
				{isLoadingEligible ? (
					<div className={css(styleSheet.emptyState)}>Loading eligible contacts...</div>
				) : maxReached ? (
					<div className={css(styleSheet.emptyState)}>
						Contact limit reached. Remove a selected contact to add more.
					</div>
				) : availableEligibleContacts.length === 0 ? (
					<div className={css(styleSheet.emptyState)}>No additional eligible contacts found.</div>
				) : (
					<ul className={css(styleSheet.contactOptions)}>
						{availableEligibleContacts.map(contact => renderContactOptionRow(contact, 'eligible'))}
					</ul>
				)}
			</section>

			<section className={css(styleSheet.section)}>
				<div className={css(styleSheet.selectedContactsHeader)}>
					<div>
						<h3 className={css(styleSheet.sectionHeader, styleSheet.sectionHeaderNoMargin)}>Selected Contacts</h3>
						<p className={css(styleSheet.sectionDescription, styleSheet.sectionDescriptionNoMargin)}>
							{selectedContacts.length}/{MAX_CONTACTS} selected for simulation.
						</p>
					</div>
					{selectedContacts.length > 0 ? (
						<Button variant='destructive-secondary' size='sm' onClick={handleClearAll}>
							Clear All
						</Button>
					) : null}
				</div>

				{selectedContacts.length === 0 ? (
					<div className={css(styleSheet.emptyState)}>No contacts selected yet.</div>
				) : (
					<div className={css(styleSheet.selectedContactsList)}>
						{selectedContacts.map(testContact => (
							<ContactCardWithSteps
								key={testContact.contact.id}
								testContact={testContact}
								templateId={templateId}
								onToggleSelect={handleToggleSelect}
								impersonationContext={impersonationContext}
								useDraft={useDraft}
								selectedContacts={selectedContacts}
								onContactsChange={onContactsChange}
							/>
						))}
					</div>
				)}

				{maxReached ? (
					<div className={css(styleSheet.contactLimit)}>Maximum of {MAX_CONTACTS} contacts selected.</div>
				) : null}
			</section>
		</div>
	);
};

const ContactCardWithSteps: React.FC<{
	testContact: TestContact;
	templateId: string;
	onToggleSelect: (contactId: string) => void;
	impersonationContext?: Api.IImpersonationContext;
	useDraft: boolean;
	selectedContacts: TestContact[];
	onContactsChange: (contacts: TestContact[]) => void;
}> = ({
	testContact,
	templateId,
	onToggleSelect,
	impersonationContext,
	useDraft,
	selectedContacts,
	onContactsChange,
}) => {
	const { data: steps } = useAutomationTemplateContactStepsQuery({
		templateId,
		contactId: testContact.contact.id,
		useDraft,
		impersonationContext,
	});

	// Update parent state when steps are loaded
	React.useEffect(() => {
		if (steps && steps !== testContact.steps) {
			const updatedContacts = selectedContacts.map(tc =>
				tc.contact.id === testContact.contact.id ? { ...tc, steps } : tc
			);
			onContactsChange(updatedContacts);
		}
	}, [steps, testContact.contact.id, testContact.steps, selectedContacts, onContactsChange]);

	const contactWithSteps: TestContact = {
		...testContact,
		steps: steps || testContact.steps,
	};

	const filterKeyFacts = (fact: string) => /insurance|policy|renewal/i.test(fact);

	return (
		<ContactCard
			contact={contactWithSteps.contact}
			onToggle={onToggleSelect}
			isSelected={contactWithSteps.isSelected}
			color={contactWithSteps.color}
			showEmail={true}
			showPhone={true}
			filterKeyFacts={filterKeyFacts}
			renderAdditionalContent={() => {
				if (contactWithSteps.isSelected && contactWithSteps.steps && contactWithSteps.steps.length > 0) {
					return <ContactProgressIndicator steps={contactWithSteps.steps} />;
				}
				return null;
			}}
		/>
	);
};
